import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { GoogleMap, GoogleMapsModule } from "@angular/google-maps";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

export interface VenueLocationValue {
  placeName: string;
  placeAddress: string;
  placeId: string;
  markerPos: google.maps.LatLngLiteral;
}

@Component({
  selector: "app-venue-location-picker",
  standalone: true,
  imports: [
    CommonModule,
    GoogleMapsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./venue-location-picker.component.html",
})
export class VenueLocationPickerComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild("searchBox") searchBox?: ElementRef<HTMLInputElement>;
  @ViewChild(GoogleMap) map?: GoogleMap;

  @Input() value: VenueLocationValue | null = null;
  @Input() venueName = "";
  @Input() disabled = false;

  @Output() valueChange = new EventEmitter<VenueLocationValue | null>();

  center: google.maps.LatLngLiteral = { lat: 33.8938, lng: 35.5018 };
  zoom = 13;
  searchText = "";
  selectedLocation: VenueLocationValue | null = null;
  errorMessage = "";
  statusMessage = "";
  isResolving = false;
  hasInteracted = false;
  mapsAvailable = true;

  readonly mapOptions: google.maps.MapOptions = {
    fullscreenControl: true,
    mapTypeId: "roadmap",
    streetViewControl: false,
    zoomControl: true,
  };
  readonly markerOptions: google.maps.MarkerOptions = {
    draggable: true,
  };

  private autocomplete?: google.maps.places.Autocomplete;
  private autocompleteListener?: google.maps.MapsEventListener;
  private locationRevision = 0;
  private lastEmittedValue: VenueLocationValue | null | undefined;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["value"] &&
      changes["value"].currentValue !== this.lastEmittedValue
    ) {
      this.applyIncomingValue(changes["value"].currentValue ?? null);
    }
  }

  ngAfterViewInit(): void {
    if (
      typeof google === "undefined" ||
      !google.maps?.places?.Autocomplete ||
      !this.searchBox?.nativeElement
    ) {
      this.mapsAvailable = false;
      this.errorMessage =
        "Google Maps could not be loaded. Refresh the page and try again.";
      this.cdr.detectChanges();
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(
      this.searchBox.nativeElement,
      {
        fields: ["formatted_address", "geometry", "name", "place_id"],
      },
    );
    this.autocompleteListener = this.autocomplete.addListener(
      "place_changed",
      () => this.zone.run(() => this.selectAutocompletePlace()),
    );
  }

  ngOnDestroy(): void {
    this.autocompleteListener?.remove();
  }

  onSearchInput(text: string): void {
    this.locationRevision += 1;
    this.searchText = text;
    this.hasInteracted = true;
    this.errorMessage = "";
    this.statusMessage = text.trim()
      ? "Select a suggestion to confirm this location."
      : "";
    this.selectedLocation = null;
    this.lastEmittedValue = null;
    this.valueChange.emit(null);
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    if (event.latLng && !this.disabled) {
      this.resolveCustomPin({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
    }
  }

  onMarkerDragEnd(event: google.maps.MapMouseEvent): void {
    if (event.latLng && !this.disabled) {
      this.resolveCustomPin({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
    }
  }

  useCurrentLocation(): void {
    this.hasInteracted = true;
    this.errorMessage = "";
    if (!navigator.geolocation) {
      this.errorMessage = "Current location is not supported by this browser.";
      return;
    }

    this.isResolving = true;
    const requestRevision = ++this.locationRevision;
    navigator.geolocation.getCurrentPosition(
      (position) =>
        this.zone.run(() => {
          if (requestRevision !== this.locationRevision) return;
          this.resolveCustomPin({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        }),
      () =>
        this.zone.run(() => {
          if (requestRevision !== this.locationRevision) return;
          this.isResolving = false;
          this.errorMessage =
            "We could not access your current location. Check your browser permission and try again.";
          this.cdr.markForCheck();
        }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  openSelectedLocation(): void {
    const marker = this.selectedLocation?.markerPos;
    if (!marker) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${marker.lat},${marker.lng}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  get showSelectionError(): boolean {
    return (
      this.hasInteracted &&
      !this.isResolving &&
      this.selectedLocation === null &&
      this.searchText.trim().length > 0
    );
  }

  private selectAutocompletePlace(): void {
    this.locationRevision += 1;
    const place = this.autocomplete?.getPlace();
    const point = place?.geometry?.location;
    this.hasInteracted = true;

    if (!place || !point) {
      this.errorMessage =
        "That result has no map position. Choose another suggestion.";
      this.selectedLocation = null;
      this.lastEmittedValue = null;
      this.valueChange.emit(null);
      return;
    }

    const markerPos = { lat: point.lat(), lng: point.lng() };
    const selection: VenueLocationValue = {
      markerPos,
      placeAddress: place.formatted_address ?? place.name ?? "",
      placeId: place.place_id ?? "",
      placeName: place.name ?? place.formatted_address ?? "",
    };
    this.searchText = selection.placeAddress || selection.placeName;
    this.confirmSelection(selection, "Google place confirmed.");
  }

  private resolveCustomPin(position: google.maps.LatLngLiteral): void {
    if (!this.mapsAvailable || typeof google === "undefined") {
      this.errorMessage = "Google Maps is unavailable.";
      return;
    }

    const requestRevision = ++this.locationRevision;
    this.isResolving = true;
    this.errorMessage = "";
    this.statusMessage = "Confirming the pin address…";
    this.selectedLocation = null;
    this.lastEmittedValue = null;
    this.valueChange.emit(null);
    this.center = position;
    this.zoom = 17;

    new google.maps.Geocoder().geocode(
      { location: position },
      (results, status) => {
        this.zone.run(() => {
          if (requestRevision !== this.locationRevision) return;
          this.isResolving = false;
          const result = status === "OK" ? results?.[0] : undefined;
          const coordinateLabel = `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`;
          const address =
            result?.formatted_address ?? `Dropped pin at ${coordinateLabel}`;
          const selection: VenueLocationValue = {
            markerPos: position,
            placeAddress: address,
            placeId: "",
            placeName: this.venueName.trim() || address,
          };
          this.searchText = address;
          this.confirmSelection(
            selection,
            result
              ? "Custom pin confirmed from its nearest address."
              : "Custom coordinates confirmed; no street address was found.",
          );
        });
      },
    );
  }

  private confirmSelection(
    selection: VenueLocationValue,
    statusMessage: string,
  ): void {
    this.selectedLocation = selection;
    this.center = selection.markerPos;
    this.zoom = 17;
    this.errorMessage = "";
    this.statusMessage = statusMessage;
    this.lastEmittedValue = selection;
    this.valueChange.emit(selection);
    this.map?.panTo(selection.markerPos);
    this.cdr.markForCheck();
  }

  private applyIncomingValue(value: VenueLocationValue | null): void {
    this.selectedLocation = value;
    this.searchText = value?.placeAddress || value?.placeName || "";
    this.errorMessage = "";
    this.statusMessage = value ? "Saved venue location." : "";
    if (value?.markerPos) {
      this.center = value.markerPos;
      this.zoom = 17;
    }
  }
}
